import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P0 Fix 2 companion — surfaces the accounts brute-force-locked in the
 * ``login_attempts`` collection. Owner sees the drilldown list; other admin
 * roles see only the aggregate counts.
 */
export const LockoutCard = ({ data, adminRole }) => {
  const active = data?.active_now ?? 0;
  const lastHour = data?.locked_last_hour ?? 0;
  const last24h = data?.locked_last_24h ?? 0;
  const isOwner = adminRole === 'owner';
  const [expanded, setExpanded] = useState(false);
  const [list, setList] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${BACKEND_URL}/api/admin/security/lockouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setList((await r.json()).locked_accounts || []);
      else setList([]);
    } catch (e) {
      logger.error('lockout list fetch failed', e);
      setList([]);
    } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { if (expanded && list === null) fetchList(); }, [expanded, list, fetchList]);

  const activeColor = active === 0 ? 'text-emerald-600' : 'text-red-600';
  const activeIcon = active === 0 ? ShieldCheck : Lock;
  const Icon = activeIcon;

  return (
    <Card data-testid="lockout-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className={`w-4 h-4 ${activeColor}`} />
          Brute-force lockouts
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Stat label="Locked now" value={active} testid="locked-now" tone={active === 0 ? 'ok' : 'alert'} />
          <Stat label="Last hour" value={lastHour} testid="locked-last-hour" tone={lastHour === 0 ? 'ok' : 'warn'} />
          <Stat label="Last 24 h" value={last24h} testid="locked-last-24h" tone="muted" />
        </div>
        {isOwner && (
          <Button
            variant="outline" size="sm" onClick={() => setExpanded((x) => !x)}
            data-testid="lockout-drilldown-toggle"
            className="w-full justify-between"
          >
            <span>{expanded ? 'Hide' : 'View'} locked accounts</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        )}
        {isOwner && expanded && (
          <LockedList list={list} loading={loadingList} />
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, testid, tone }) => {
  const toneClass = { ok: 'text-emerald-600', warn: 'text-amber-600', alert: 'text-red-600', muted: 'text-slate-700' }[tone] || 'text-slate-700';
  return (
    <div className="text-center p-3 rounded-md bg-slate-50">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${toneClass}`} data-testid={testid}>{value}</p>
    </div>
  );
};

const LockedList = ({ list, loading }) => {
  if (loading) return <p className="text-sm text-slate-500 mt-3">Loading…</p>;
  if (!list || list.length === 0) return <p className="text-sm text-slate-500 mt-3" data-testid="lockout-list-empty">No accounts currently locked.</p>;
  return (
    <div className="mt-3 divide-y" data-testid="lockout-list">
      {list.map((row, i) => (
        <div key={i} className="py-2 text-sm flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">{row.email}</p>
            <p className="text-xs text-slate-500">IP {row.last_ip || 'unknown'} · {row.failed_attempts} failed attempts</p>
          </div>
          <p className="text-xs text-slate-600 whitespace-nowrap">until {new Date(row.locked_until).toLocaleTimeString()}</p>
        </div>
      ))}
    </div>
  );
};
